import { FC, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useConfirm from 'src/hooks/useConfirm';
import useReceiptDetails from 'src/hooks/useReceiptDetails';
import useRedirect from 'src/hooks/useRedirect';
import useCreateInvoiceApi, { CreateInvoiceResponse } from 'src/services/useCreateInvoiceApi';
import { useContextSelector } from 'use-context-selector';
import { PoRelatedDocumentsContext } from '../po_related_documents/PoRelatedDocumentsContext';

interface CreateLinkedInvoiceProps {
  receiptId: string;
  isCurrentDocLinked: boolean;
}

const CreateLinkedInvoice: FC<CreateLinkedInvoiceProps> = ({ receiptId, isCurrentDocLinked }) => {
  const { getUrl, redirect } = useRedirect();
  const { receiptData, refreshPage, getMessages, isReceiptActionButtonsDisabled } =
    useReceiptDetails();
  const getPoDocuments = useContextSelector(PoRelatedDocumentsContext, (value) => value.refresh);
  const poDetails = useContextSelector(PoRelatedDocumentsContext, (value) => value.poDetails);
  const { confirm, onClose } = useConfirm();
  const { createInvoice, isCreateInvoiceLoading } = useCreateInvoiceApi();
  const [searchParams] = useSearchParams();
  const { createInvoice: createInvoiceButtonState, companyId } = receiptData;

  const handleCreateInvoice = useCallback(() => {
    if ((poDetails?.allocations?.length || 0) > 1) {
      confirm({
        title: 'Multiple Period Allocation',
        content:
          'The PO associated with this Invoice is assigned to Multiple Periods. Please choose either Multiple or Single Period Assignment for this Invoice.',
        cancelButtonText: 'Assign Single Period',
        confirmButtonText: 'Assign Multiple Periods',
        cancelColor: 'info',
        confirmColor: 'info',
        width: '500px',
        closeIcon: true,
        onClose,
      }).then(async (useMultiplePeriod: boolean) => {
        const response: CreateInvoiceResponse = await createInvoice({
          companyId: companyId.toString() || '',
          poId: searchParams.get('poid') || '',
          receiptId: parseInt(receiptId),
          useSinglePeriod: !useMultiplePeriod,
        });
        getPoDocuments();
        redirect(
          getUrl('invoiceDetails')(String(response.invoiceId), String(searchParams.get('poid')))
        );
      });
    } else {
      createInvoice({
        companyId: companyId.toString() || '',
        poId: searchParams.get('poid') || '',
        receiptId: parseInt(receiptId),
        useSinglePeriod: true,
      })
        .then((response: CreateInvoiceResponse) => {
          getPoDocuments();
          redirect(
            getUrl('invoiceDetails')(String(response.invoiceId), String(searchParams.get('poid')))
          );
        })
        .catch(console.error);
    }
  }, [
    companyId,
    confirm,
    createInvoice,
    getPoDocuments,
    getUrl,
    onClose,
    poDetails?.allocations?.length,
    receiptId,
    redirect,
    searchParams,
  ]);

  const handleEDI = useCallback(async () => {
    await confirm({
      title: 'EDI 810 Enabled',
      content: 'There may be a pending transmission and a duplicate record may be created',
      cancelButtonText: 'Cancel',
      cancelColor: 'info',
      confirmButtonText: 'Continue',
      width: '500px',
    }).then((isConfirm) => {
      if (isConfirm) {
        handleCreateInvoice();
      }
    });
  }, [confirm, handleCreateInvoice]);

  const handleCreateInvoiceClick = useCallback(() => {
    if (poDetails?.isEDIReceipt) {
      handleEDI();
    } else {
      handleCreateInvoice();
    }
    refreshPage();
  }, [handleCreateInvoice, handleEDI, poDetails?.isEDIReceipt, refreshPage]);

  const messages = useMemo(
    () => [...(isCurrentDocLinked ? ['Receipt is Linked to an Invoice.'] : [])],
    [isCurrentDocLinked]
  );

  const isDisabled = isReceiptActionButtonsDisabled(!!messages.length);

  return (
    <PopUp
      content={<PopUpContentWrapper type="Error" contents={getMessages(messages)} />}
      show={isDisabled}
    >
      <Button
        onClick={handleCreateInvoiceClick}
        loading={isCreateInvoiceLoading}
        buttonState={createInvoiceButtonState}
        disabled={isDisabled}
      >
        Create Linked Invoice
      </Button>
    </PopUp>
  );
};

export default CreateLinkedInvoice;
